import DOMPurify from "dompurify";

/**
 * @param value - The value to pluralize
 * @param label - The label to pluralize
 * @param pluralize - Whether to pluralize the label
 * @param pluralLabel - The plural label to use if pluralize is true
 * @returns The pluralized label
 */
export const pluralise = (
	value: number,
	label: string,
	pluralize = true,
	pluralLabel?: string,
) => {
	if (!pluralize) {
		return label;
	}
	return value === 1 ? label : (pluralLabel ?? `${label}s`);
};

/**
 * Replaces straight quotes with smart (curly) quotes and double dashes with em dashes.
 * Equivalent to HTML.clean() from foundation/html.js.
 */
export const smartenText = (text: string): string =>
	text
		.replace(/(\s)--(\s)/g, "$1\u2014$2")
		.replace(/(\w{2,})--(\w{2,})/g, "$1\u2014$2")
		.replace(/(^|[-\u2014\s(["])"/g, "$1\u201C")
		.replace(/"/g, "\u201D")
		.replace(/(^|[-\u2014/[(\s])'/g, "$1\u2018")
		.replace(/(\s)'(\d)/g, "$1\u2019$2")
		.replace(/'/g, "\u2019");

/** Applies {@link smartenText} to string nodes in a JSX tree, preserving elements. */
export const smartenChildren = (node: unknown): unknown => {
	if (typeof node === "string") {
		return smartenText(node);
	}
	if (Array.isArray(node)) {
		return node.map(smartenChildren);
	}
	return node;
};

const UNSAFE_URL_SCHEME = /^(javascript|data|vbscript|file):/i;

/** Escape a string for use inside an HTML attribute value. */
function escapeHtmlAttribute(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
}

function sanitizeLinkHref(url: string): string | null {
	const trimmed = url.trim();
	if (!trimmed || UNSAFE_URL_SCHEME.test(trimmed) || trimmed.startsWith("//")) {
		return null;
	}

	if (trimmed.startsWith("#")) {
		return escapeHtmlAttribute(trimmed);
	}

	if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
		return escapeHtmlAttribute(trimmed);
	}

	try {
		const parsed = new URL(trimmed);
		const protocol = parsed.protocol.toLowerCase();
		if (
			protocol === "https:" ||
			protocol === "http:" ||
			protocol === "mailto:"
		) {
			return escapeHtmlAttribute(trimmed);
		}
	} catch {
		// Relative paths without a leading slash (e.g. docs/page).
		if (!trimmed.includes(":") && /^[\w./%-]+$/i.test(trimmed)) {
			return escapeHtmlAttribute(trimmed);
		}
	}

	return null;
}

function sanitizeImageSrc(url: string): string | null {
	const trimmed = url.trim();
	if (!trimmed || UNSAFE_URL_SCHEME.test(trimmed) || trimmed.startsWith("//")) {
		return null;
	}

	if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
		return escapeHtmlAttribute(trimmed);
	}

	try {
		const parsed = new URL(trimmed);
		const protocol = parsed.protocol.toLowerCase();
		if (protocol === "https:" || protocol === "http:") {
			return escapeHtmlAttribute(trimmed);
		}
	} catch {
		return null;
	}

	return null;
}

const MARKDOWN_HTML_ALLOWED_TAGS = [
	"a",
	"blockquote",
	"code",
	"em",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"hr",
	"img",
	"li",
	"ol",
	"p",
	"pre",
	"s",
	"small",
	"strong",
	"sub",
	"sup",
	"table",
	"td",
	"th",
	"tr",
	"ul",
	"big",
];

const MARKDOWN_HTML_ALLOWED_ATTR = [
	"alt",
	"href",
	"rel",
	"src",
	"start",
	"style",
];

/** Final pass: strip anything outside our markdown HTML allowlist. */
function sanitizeMarkdownHtml(html: string): string {
	return DOMPurify.sanitize(html, {
		ALLOWED_TAGS: MARKDOWN_HTML_ALLOWED_TAGS,
		ALLOWED_ATTR: MARKDOWN_HTML_ALLOWED_ATTR,
	});
}

/**
 * markdown function from drawdown.js
 * (c) Adam Leggett
 */
export const formatMarkdown = (src: string): string => {
	var rx_lt = /</g;
	var rx_gt = />/g;
	var rx_space = /\t|\r|\uf8ff/g;
	var rx_escape = /\\([\\|`*_{}[\]()#+\-~])/g;
	var rx_hr = /^([*\-=_] *){3,}$/gm;
	var rx_blockquote = /\n *&gt; *([^]*?)(?=(\n|$){2})/g;
	var rx_list =
		/\n( *)(?:[*\-+]|((\d+)|([a-z])|[A-Z])[.)]) +([^]*?)(?=(\n|$){2})/g;
	var rx_listjoin = /<\/(ol|ul)>\n\n<\1(?: [^>]*)?>/g;
	var rx_highlight =
		/(^|[^A-Za-z\d\\])(([*_])|(~(?=~))|(--)|(\+\+)|`)(\2?)([^<]*?)\2\7(?!\2)(?=\W|_|$)/g;
	// Pandoc's rule: no whitespace between the markers, so a free-standing `~`
	// meaning "approximately" (`~€5 or ~€6`) never opens a subscript.
	var rx_subsup = /(<code>[^]*?<\/code>)|(^|[^\\])(~|\^)([^\s~^<\\]+)\3/g;
	var rx_code = /\n((```|~~~).*\n?([^]*?)\n?\2|((	.*?\n)+))/g;
	var rx_link = /((!?)\[(.*?)\]\((.*?)( ".*")?\)|\\([\\`*_{}[\]()#+\-.!~]))/g;
	var rx_table = /\n(( *\|.*?\| *\n)+)/g;
	var rx_thead = /^.*\n( *\|( *:?-+:?-+:? *\|)* *\n|)/;
	var rx_row = /.*\n/g;
	var rx_cell = /\||(.*?[^\\])\|/g;
	var rx_heading = /(?=^|>|\n)([>\s]*?)(#{1,6}) (.*?)( #*)? *(?=\n|$)/g;
	var rx_para = /(?=^|>|\n)\s*\n+([^<]+?)\n+\s*(?=\n|<|$)/g;
	var rx_stash = /-\d+\uf8ff/g;

	const allowedHtmlTags = new Set(MARKDOWN_HTML_ALLOWED_TAGS);

	function element(tag: string, content: string): string {
		const normalizedTag = tag.toLowerCase();
		if (!allowedHtmlTags.has(normalizedTag)) {
			return content;
		}
		return `<${normalizedTag}>${content}</${normalizedTag}>`;
	}

	function blockquote(src: string): string {
		return src.replace(rx_blockquote, (_all: string, content: string) =>
			element(
				"blockquote",
				blockquote(highlight(content.replace(/^ *&gt; */gm, ""))),
			),
		);
	}

	function list(src: string): string {
		return src.replace(
			rx_list,
			(
				_all: string,
				ind: string,
				ol: string,
				num: string,
				low: string,
				content: string,
			) => {
				var entry = element(
					"li",
					highlight(
						content
							.split(
								RegExp(`\n ?${ind}(?:(?:\\d+|[a-zA-Z])[.)]|[*\\-+]) +`, "g"),
							)
							.map(list)
							.join("</li><li>"),
					),
				);

				return ol
					? num
						? `\n<ol start="${escapeHtmlAttribute(ol)}">${entry}</ol>`
						: `\n<ol start="${escapeHtmlAttribute(String(parseInt(ol, 36) - 9))}" style="list-style-type:${low ? "low" : "upp"}er-alpha">${entry}</ol>`
					: `\n${element("ul", entry)}`;
			},
		);
	}

	function highlight(src: string, subSup = true): string {
		const html = src.replace(
			rx_highlight,
			(
				_all: string,
				prefix: string,
				_p1: string,
				emp: string,
				strike: string,
				small: string,
				big: string,
				p2: string,
				content: string,
			) => {
				const tag = emp
					? p2
						? "strong"
						: "em"
					: strike
						? "s"
						: small
							? "small"
							: big
								? "big"
								: "code";
				return (
					prefix + element(tag, highlight(content, subSup && tag !== "code"))
				);
			},
		);
		if (!subSup) {
			return html;
		}
		return html.replace(
			rx_subsup,
			(
				all: string,
				code: string | undefined,
				prefix: string,
				marker: string,
				content: string,
			) =>
				code
					? all
					: prefix + element(marker === "~" ? "sub" : "sup", content),
		);
	}

	function unesc(str: string): string {
		return str.replace(rx_escape, "$1");
	}

	const stash: string[] = [];
	let si = 0;

	src = `\n${src}\n`;

	src = src.replace(rx_lt, "&lt;");
	src = src.replace(rx_gt, "&gt;");
	src = src.replace(rx_space, "  ");

	// blockquote
	src = blockquote(src);

	// horizontal rule
	src = src.replace(rx_hr, "<hr/>");

	// list
	src = list(src);
	src = src.replace(rx_listjoin, "");

	// code
	src = src.replace(
		rx_code,
		(_all: string, _p1: string, _p2: string, p3: string, p4: string) => {
			stash[--si] = element(
				"pre",
				element("code", p3 || p4.replace(/^	/gm, "")),
			);
			return `${si}\uf8ff`;
		},
	);

	// link or image
	src = src.replace(
		rx_link,
		(
			_all: string,
			_p1: string,
			p2: string,
			p3: string,
			p4: string,
			_p5: string,
			p6: string,
		) => {
			if (p4) {
				if (p2) {
					const imageSrc = sanitizeImageSrc(p4);
					stash[--si] = imageSrc
						? `<img src="${imageSrc}" alt="${escapeHtmlAttribute(p3)}"/>`
						: escapeHtmlAttribute(unesc(highlight(p3)));
				} else {
					const href = sanitizeLinkHref(p4);
					stash[--si] = href
						? `<a href="${href}" rel="noopener noreferrer">${unesc(highlight(p3))}</a>`
						: unesc(highlight(p3));
				}
			} else {
				stash[--si] = p6;
			}
			return `${si}\uf8ff`;
		},
	);

	// table
	src = src.replace(rx_table, (_all: string, table: string) => {
		const theadMatch = table.match(rx_thead);
		const sep = theadMatch?.[1];
		if (sep === undefined) {
			return _all;
		}
		return `\n${element(
			"table",
			table.replace(rx_row, (row: string, ri: number) =>
				row === sep
					? ""
					: element(
							"tr",
							row.replace(
								rx_cell,
								(_cellMatch: string, cell: string, ci: number) =>
									ci
										? element(
												sep && !ri ? "th" : "td",
												unesc(highlight(cell || "")),
											)
										: "",
							),
						),
			),
		)}`;
	});

	// heading
	src = src.replace(
		rx_heading,
		(_all: string, _, p1: string, p2: string) =>
			_ + element(`h${p1.length}`, unesc(highlight(p2))),
	);

	// paragraph
	src = src.replace(rx_para, (_all: string, content: string) =>
		element("p", unesc(highlight(content))),
	);

	// smart quotes (text nodes only — preserves HTML tags and inline code)
	src = src.replace(
		/<code>[^]*?<\/code>|(<[^>]*>)|([^<]+)/g,
		(all: string, _tag: string | undefined, text: string | undefined) => {
			if (!text) return all;
			return text
				.replace(/(^|[-\u2013\u2014\s([{])"/g, "$1\u201c")
				.replace(/"/g, "\u201d")
				.replace(/(^|[-\u2013\u2014\s([{])'/g, "$1\u2018")
				.replace(/'/g, "\u2019");
		},
	);

	// stash
	src = src.replace(rx_stash, (all: string) => stash[parseInt(all, 10)] ?? "");

	return sanitizeMarkdownHtml(src.trim());
};

const BLOCK_DOUBLE_NEWLINE = new Set([
	"P",
	"H1",
	"H2",
	"H3",
	"H4",
	"H5",
	"H6",
	"TABLE",
	"PRE",
	"HR",
]);
const BLOCK_SINGLE_NEWLINE = new Set([
	"LI",
	"TR",
	"DIV",
	"UL",
	"OL",
	"BLOCKQUOTE",
]);

/**
 * Converts an HTML element tree to well-formatted plain text.
 * Block elements get appropriate newline spacing; inline elements
 * contribute their text content directly.
 */
export const htmlToPlainText = (root: HTMLElement): string => {
	let result = "";

	for (const node of root.childNodes) {
		if (node.nodeType === Node.TEXT_NODE) {
			result += node.textContent ?? "";
		} else if (node.nodeType === Node.ELEMENT_NODE) {
			const el = node as HTMLElement;
			const tag = el.tagName;

			if (tag === "BR") {
				result += "\n";
				continue;
			}

			const inner = htmlToPlainText(el);
			if (BLOCK_DOUBLE_NEWLINE.has(tag)) {
				if (result.replace(/\n*$/, "").length > 0) {
					result = result.replace(/\n*$/, "\n\n");
				}
				result += inner.replace(/\n*$/, "\n");
			} else if (BLOCK_SINGLE_NEWLINE.has(tag)) {
				result = result.replace(/\n*$/, "\n") + inner.replace(/\n*$/, "\n");
			} else {
				result += inner;
			}
		}
	}

	return result;
};
